import React, { useEffect, useState } from 'react';
import Skeleton from '../components/Skeleton.js';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';

export default function Courses() {
  const navigate = useNavigate();

  const [coursesList, setCoursesList] = useState<any[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<any[]>([]);
  const [selectedGamemode, setSelectedGamemode] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchCourses() {
      try {
        const response = await api.get('/api/courses');
        const coursesData = response.data || [];

        const coursesWithProgress = await Promise.all(
          coursesData.map(async (course: any) => {
            try {
              const progressRes = await api.get(`/api/courses/${course.id}/progress`);
              return {
                ...course,
                percentage: progressRes.data?.percentage || 0
              };
            } catch (err) {
              return {
                ...course,
                percentage: 0
              };
            }
          })
        );

        setCoursesList(coursesWithProgress);
        setFilteredCourses(coursesWithProgress);
      } catch (err) {
        console.error('Failed to fetch courses:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchCourses();
  }, []);

  useEffect(() => {
    let result = coursesList;

    if (selectedGamemode !== 'all') {
      result = result.filter(c => c.gamemode === selectedGamemode);
    }

    if (selectedDifficulty !== 'all') {
      result = result.filter(c => c.difficulty === selectedDifficulty);
    }

    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        c => c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
      );
    }

    setFilteredCourses(result);
  }, [selectedGamemode, selectedDifficulty, searchQuery, coursesList]);

  const getGamemodeColor = (gm: string) => {
    switch (gm) {
      case 'mace':
      case 'mace_rockets':
        return 'bg-[rgba(255,191,0,0.15)] text-[#ffbf00]';
      case 'sword':
      case 'cpvp':
        return 'bg-[rgba(224,60,60,0.15)] text-[#e03c3c]';
      case 'axe_shield':
        return 'bg-[rgba(76,175,80,0.15)] text-[#4caf50]';
      default:
        return 'bg-[rgba(255,255,255,0.08)] text-on-surface-variant';
    }
  };

  const getGamemodeIcon = (gm: string) => {
    switch (gm) {
      case 'sword': return '⚔️';
      case 'mace': return '🔨';
      case 'mace_rockets': return '🚀';
      case 'cpvp': return '💎';
      case 'axe_shield': return '🛡️';
      case 'nethpot': return '🧪';
      case 'tank': return '🪖';
      default: return '🎮';
    }
  };

  const getGamemodeLabel = (gm: string) => {
    if (gm === 'cpvp') return 'Crystal PvP';
    if (gm === 'axe_shield') return 'Axe & Shield';
    if (gm === 'mace_rockets') return 'Mace Rockets';
    return gm.charAt(0).toUpperCase() + gm.slice(1);
  };

  if (isLoading) {
    return (
      <div className="flex-1 min-h-screen bg-background flex items-center justify-center text-on-surface-variant font-medium">
        <div className="w-full max-w-6xl px-gutter">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-48 rounded-lg overflow-hidden">
                <Skeleton />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface">
      <div className="flex justify-between items-center mb-8 select-none">
        <div>
          <h1 className="font-headline-lg text-headline-lg text-on-surface mb-2">Training Courses</h1>
          <p className="text-on-surface-variant text-body-sm">
            Select a gamemode course to begin custom drills and lessons.
          </p>
        </div>
      </div>

      {/* Filters Panel */}
      <div className="flex flex-wrap gap-4 items-center bg-[#141414] thin-border p-4 rounded-lg mb-8 select-none">
        {/* Search */}
        <div className="flex items-center gap-2 px-3 py-2 bg-surface-container rounded thin-border text-on-surface-variant text-[13px] flex-1 min-w-[200px]">
          <span className="material-symbols-outlined text-[18px]">search</span>
          <input
            type="text"
            placeholder="Search catalog..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-transparent border-none outline-none text-on-surface text-[13px] p-0 w-full focus:ring-0"
          />
        </div>

        {/* Gamemode Selector */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className="text-[10px] uppercase tracking-wider text-on-surface-variant font-bold">
            Gamemode
          </label>
          <select
            value={selectedGamemode}
            onChange={(e) => setSelectedGamemode(e.target.value)}
            className="bg-surface-container border border-white/10 rounded px-2.5 py-1.5 text-on-surface text-[13px] outline-none focus:border-error focus:ring-0"
          >
            <option value="all">All Modes</option>
            <option value="sword">Sword</option>
            <option value="mace">Mace</option>
            <option value="mace_rockets">Mace Rockets</option>
            <option value="cpvp">Crystal PvP</option>
            <option value="axe_shield">Axe & Shield</option>
            <option value="nethpot">Nethpot</option>
            <option value="tank">Tank</option>
          </select>
        </div>

        {/* Difficulty Selector */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className="text-[10px] uppercase tracking-wider text-on-surface-variant font-bold">
            Difficulty
          </label>
          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
            className="bg-surface-container border border-white/10 rounded px-2.5 py-1.5 text-on-surface text-[13px] outline-none focus:border-error focus:ring-0"
          >
            <option value="all">All Skills</option>
            <option value="beginner">Beginner</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
      </div>

      {/* Grid of Courses */}
      {filteredCourses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
          {filteredCourses.map((course, idx) => {
            const lessonsCount = course.lessons?.length || 0;
            return (
              <div
                key={course.id}
                onClick={() => navigate(`/courses/${course.id}`)}
                className="bg-[#141414] thin-border rounded-lg overflow-hidden group hover:border-white/20 transition-all cursor-pointer flex flex-col justify-between fade-in-card"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div>
                  {/* Gamemode icon header — no thumbnail */}
                  <div className="h-24 relative bg-[#151515] flex items-center justify-center select-none">
                    <span className="text-4xl">
                      {getGamemodeIcon(course.gamemode)}
                    </span>
                    <div className={`absolute top-3 left-3 px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase ${
                      course.difficulty === 'beginner'
                        ? 'bg-[rgba(76,175,80,0.15)] text-[#4caf50]'
                        : 'bg-[rgba(255,191,0,0.15)] text-[#ffbf00]'
                    }`}>
                      {course.difficulty}
                    </div>
                  </div>

                  <div className="p-stack-md">
                    <div className="flex items-center gap-2 mb-2 select-none">
                      <span
                        className={`px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase ${getGamemodeColor(course.gamemode)}`}
                      >
                        {getGamemodeLabel(course.gamemode)}
                      </span>
                      <span className="text-on-surface-variant text-[11px]">
                        {lessonsCount} lesson{lessonsCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <h4 className="font-headline-sm text-headline-sm text-on-surface mb-2 truncate">
                      {course.title}
                    </h4>
                    <p className="text-on-surface-variant text-[12px] line-clamp-3">
                      {course.description}
                    </p>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="px-stack-md pb-stack-md select-none">
                  <div className="flex justify-between text-[11px] text-on-surface-variant mb-1 font-mono">
                    <span>Progress</span>
                    <span>{course.percentage ?? 0}%</span>
                  </div>
                  <div className="w-full h-1 bg-surface-container rounded-full overflow-hidden">
                    <div
                      className="h-full bg-error rounded-full transition-all duration-300"
                      style={{ width: `${course.percentage ?? 0}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-[#141414] thin-border rounded-lg p-stack-lg text-center text-on-surface-variant select-none">
          No courses match your active search filters.
        </div>
      )}
    </div>
  );
}